import {faPlus, faXmark} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Flex, Input, Select, Space, Tooltip, Tour, TourProps} from 'antd';
import {keyBy, merge, values} from 'lodash';
import {memo, useEffect, useMemo, useRef, useState} from 'react';
import {z as r} from 'zod';

import {useBrands} from '../../../../provider/brands/brand-hook';
import {useLocale} from '../../../../provider/locale/locale-hook';
import {Locale} from '../../../../provider/locale/localization';
import {useModal} from '../../../../provider/modal/modal-hook';
import {usePersonality} from '../../../../provider/personality/use-personality';
import {useSnackbar} from '../../../../provider/snackbar/snackbar-hook';
import {useSystem} from '../../../../provider/system/system-hook';
import {
    ReviewPresenceActionEnum,
    ReviewPresenceBlockedActionEnum,
} from '../../../../service/reviews/review-realtime-presence/review-realtime-presence-type';
import {useReviewPresenceActivity} from '../../../../service/reviews/review-realtime-presence/use-review-presence-activity-hook';
import {
    ManageReviewStatusError,
    ReviewStatusType,
    updateReviewStatus,
    useCreateReviewStatus,
    useDeleteReviewStatus,
} from '../../../../service/reviews/reviews-statuses';
import {
    canShowStatusDeleteControl,
    isCustomStatusDeletable,
    isDefaultStatusDeletable,
    isSystemDefaultStatus,
} from '../../../../service/reviews/reviews-statuses-helper';
import {ReviewDataType} from '../../../../service/reviews/reviews-type';
import {useVisibleReviewStatuses} from '../../../../service/reviews/use-visible-review-statuses';
import {useUserRoles} from '../../../../service/user-management/user-management-user';
import {handleFilterOption} from '../../../../util/antd/select-helper';
import {classNames} from '../../../../util/css';
import {HttpError} from '../../../../util/error';
import {getFromLocalStorage, saveToLocalStorage} from '../../../../util/local-storage';
import {ReviewPresenceBlockedTooltip} from '../review-realtime-presence/review-presence-blocked-tooltip/review-presence-blocked-tooltip';

import {OverflowTitle} from './overflow-title';
import {IS_SHOW_CUSTOM_STATUS_TOUR_LOCALSTORAGE_KEY} from './review-status-const';
import * as styles from './review-status.scss';

const {Option} = Select;

type PropsType = {
    updateReview: (reviewId: number, updateData: Partial<ReviewDataType>) => void;
    reviewId: number;
    brandId: number;
    processStatus: string;
    className?: string;
    onChange?: () => void;
    isFirstReview?: boolean;
    isPresenceLocked?: boolean;
    /** Передаётся с list-страницы: тур показывается только у видимого селекта (в хедере при ширине >500px, в теле при ≤500px) */
    isTourEligible?: boolean;
};

// eslint-disable-next-line max-statements, complexity
export const ReviewStatus = memo(function ReviewStatusInner(props: PropsType): JSX.Element {
    const {
        updateReview,
        reviewId,
        brandId,
        processStatus,
        className,
        onChange,
        isFirstReview,
        isPresenceLocked,
        isTourEligible,
    } = props;

    const {screen} = useSystem();
    const isTourEligibleByDevice =
        !screen.isMobile && (typeof isTourEligible === 'boolean' ? isTourEligible : Boolean(isFirstReview));

    const {localeName, getLocalizedString} = useLocale();
    const {snackbar} = useSnackbar();
    const {modal} = useModal();
    const {data: ownUser} = useUserRoles();
    const isOwner = Boolean(ownUser?.role?.isOwner);
    const {
        allStatuses,
        visibleStatuses,
        getStatusByCode,
        hideStatusCode,
        isReviewStatusListInProgress,
        isSavingHiddenStatuses,
    } = useVisibleReviewStatuses(localeName);
    const {isLoading} = useBrands();
    const {mutateAsync: createStatus, isLoading: isCreatingStatusInProgress} = useCreateReviewStatus(localeName);
    const {mutateAsync: deleteStatus, isLoading: isDeletingStatusInProgress} = useDeleteReviewStatus(localeName);
    const createButtonRef = useRef<HTMLButtonElement | null>(null);

    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const [innerStatus, setInnerStatus] = useState<string>(processStatus);
    const [isOpen, setIsOpen] = useState(false);
    const [isCreatingStatus, setIsCreatingStatus] = useState<boolean>(false);
    const [newStatusValue, setNewStatusValue] = useState<string>('');
    const [newlyCreatedStatusCode, setNewlyCreatedStatusCode] = useState<string | null>(null);
    const [isTourOpen, setIsTourOpen] = useState<boolean>(false);

    const presenceActivityOptions = useMemo(
        () => ({
            reviewId,
            brandId,
            action: ReviewPresenceActionEnum.Status,
        }),
        [brandId, reviewId]
    );

    useReviewPresenceActivity([presenceActivityOptions], isOpen);

    const status = useMemo(() => {
        return getStatusByCode(innerStatus);
    }, [getStatusByCode, innerStatus]);

    const isStatusMissingFromList = useMemo(() => {
        if (allStatuses.length === 0) {
            return false;
        }

        return !allStatuses.some((statusItem) => statusItem.statusCode === innerStatus);
    }, [allStatuses, innerStatus]);

    const {reviewStatusesMapping} = usePersonality();

    const mappedStatuses = useMemo(() => {
        if (!reviewStatusesMapping) {
            return null;
        }

        const results = values(
            merge(keyBy(reviewStatusesMapping, 'statusCode'), keyBy(allStatuses, 'statusCode'))
        ).filter((item) => item.availableStatus);

        return new Map<string, Array<string>>(results.map((item) => [item.statusCode, item.availableStatus]));
    }, [reviewStatusesMapping, allStatuses]);

    const shouldShowTour = isTourEligibleByDevice && !mappedStatuses && isOwner;

    useEffect(() => {
        setInnerStatus(processStatus);
    }, [processStatus]);

    useEffect(() => {
        let timeoutId: NodeJS.Timeout | null = null;

        if (newlyCreatedStatusCode && isOpen && allStatuses.length > 0) {
            timeoutId = setTimeout(() => {
                const dropdownElement = document.querySelector('.ant-select-dropdown:not(.ant-select-dropdown-hidden)');

                if (dropdownElement) {
                    const listElement = dropdownElement.querySelector('.rc-virtual-list-holder');

                    if (listElement) {
                        listElement.scrollTop = listElement.scrollHeight;
                    }
                }

                setNewlyCreatedStatusCode(null);
            }, 100);
        }

        return () => {
            if (timeoutId !== null) {
                clearTimeout(timeoutId);
            }
        };
    }, [newlyCreatedStatusCode, isOpen, allStatuses.length]);

    useEffect(() => {
        let timer: NodeJS.Timeout | null = null;

        if (
            shouldShowTour &&
            !isReviewStatusListInProgress &&
            !isLoading &&
            getFromLocalStorage(IS_SHOW_CUSTOM_STATUS_TOUR_LOCALSTORAGE_KEY, r.boolean(), true)
        ) {
            timer = setTimeout(() => {
                setIsOpen(true);
            }, 1300);
        }

        return () => {
            if (timer !== null) {
                clearTimeout(timer);
            }
        };
    }, [shouldShowTour, isReviewStatusListInProgress, isLoading]);

    useEffect(() => {
        let timer: NodeJS.Timeout | null = null;

        if (
            isOpen &&
            shouldShowTour &&
            getFromLocalStorage(IS_SHOW_CUSTOM_STATUS_TOUR_LOCALSTORAGE_KEY, r.boolean(), true)
        ) {
            timer = setTimeout(() => {
                setIsTourOpen(true);
            }, 600);
        }

        return () => {
            if (timer !== null) {
                clearTimeout(timer);
            }
        };
    }, [isOpen, shouldShowTour]);

    function handleCloseTour() {
        setIsTourOpen(false);
        saveToLocalStorage(false, IS_SHOW_CUSTOM_STATUS_TOUR_LOCALSTORAGE_KEY);
    }

    const tourSteps: TourProps['steps'] = useMemo(
        () => [
            {
                title: <Locale stringKey="REVIEWS__STATUS__TOUR_TITLE" />,
                description: <Locale stringKey="REVIEWS__STATUS__TOUR_DESCRIPTION" />,
                placement: 'left',
                target: () => createButtonRef.current ?? document.body,
            },
        ],
        []
    );

    async function onStatusSelect(selectedStatus: string) {
        try {
            setIsUpdating(true);
            setInnerStatus(selectedStatus);
            await updateReviewStatus(reviewId, {status: selectedStatus});
            await updateReview(reviewId, {processStatus: selectedStatus});

            if (onChange) {
                onChange();
            }
        } catch (error: unknown) {
            const description =
                error instanceof ManageReviewStatusError ? (
                    <span>{error.jsonData.status}</span>
                ) : (
                    <Locale stringKey="ERROR__SOMETHING_WENT_WRONG_DETAILS" />
                );

            snackbar.error({
                message: <Locale stringKey="ERROR__SOMETHING_WENT_WRONG" />,
                description,
            });

            if (!(error instanceof HttpError)) {
                throw error;
            }
        } finally {
            setIsUpdating(false);
        }
    }

    function handleCreateStatusClick() {
        setIsCreatingStatus(true);
        handleCloseTour();
    }

    function handleCancelCreateStatus() {
        setIsCreatingStatus(false);
        setNewStatusValue('');
    }

    async function handleSaveNewStatus() {
        if (!newStatusValue.trim()) {
            return;
        }

        const createdStatus = await createStatus({title: newStatusValue});

        setIsCreatingStatus(false);
        setNewStatusValue('');

        if (createdStatus) {
            setNewlyCreatedStatusCode(createdStatus.statusCode);
            setIsOpen(true);
        }
    }

    function handleCloseSelect(value: boolean) {
        setIsOpen(value);

        if (!value && isTourOpen) {
            handleCloseTour();
        }
    }

    function openDeleteCustomStatusModal(statusCode: string) {
        const availableStatuses = visibleStatuses.filter((statusItem) => statusItem.statusCode !== statusCode);
        const defaultReplacementStatus = availableStatuses.find((statusItem) => statusItem.isDefault);
        let selectedReplacementStatus = defaultReplacementStatus?.statusCode || availableStatuses[0]?.statusCode || '';

        modal.confirm({
            cancelButtonProps: {type: 'default'},
            cancelText: <Locale stringKey="BUTTON__CANCEL" />,
            content: (
                <div>
                    <div className={styles.ReviewStatus_modalDescription}>
                        <Locale stringKey="REVIEWS__STATUS__DELETE_MODAL_DESCRIPTION" />
                    </div>
                    <Select
                        className={styles.ReviewStatus_modalSelect}
                        defaultValue={selectedReplacementStatus}
                        onChange={(value) => {
                            selectedReplacementStatus = value;
                        }}
                        placeholder={getLocalizedString('REVIEWS__STATUS__DELETE_MODAL_SELECT_PLACEHOLDER')}
                    >
                        {availableStatuses.map((statusItem) => (
                            <Option key={statusItem.statusCode} value={statusItem.statusCode}>
                                {statusItem.title}
                            </Option>
                        ))}
                    </Select>
                </div>
            ),
            maskClosable: true,
            okButtonProps: {type: 'primary', danger: true},
            okText: <Locale stringKey="REVIEWS__STATUS__DELETE_MODAL_CONFIRM" />,
            title: <Locale stringKey="REVIEWS__STATUS__DELETE_MODAL_TITLE" />,
            onOk: async () => {
                if (selectedReplacementStatus) {
                    await deleteStatus({
                        statusCode,
                        replacementStatusCode: selectedReplacementStatus,
                    });
                }
            },
        });
    }

    async function hideDefaultStatus(statusCode: string, replacementStatusCode: string) {
        await hideStatusCode(statusCode);

        if (processStatus !== statusCode) {
            snackbar.success(<Locale stringKey="REVIEWS__STATUS__DELETE_SUCCESS" />);

            return;
        }

        try {
            setIsUpdating(true);
            setInnerStatus(replacementStatusCode);
            await updateReviewStatus(reviewId, {status: replacementStatusCode});
            updateReview(reviewId, {processStatus: replacementStatusCode});
            snackbar.success(<Locale stringKey="REVIEWS__STATUS__DELETE_SUCCESS" />);

            if (onChange) {
                onChange();
            }
        } catch (error: unknown) {
            const description =
                error instanceof ManageReviewStatusError ? (
                    <span>{error.jsonData.status}</span>
                ) : (
                    <Locale stringKey="ERROR__SOMETHING_WENT_WRONG_DETAILS" />
                );

            snackbar.error({
                message: <Locale stringKey="ERROR__SOMETHING_WENT_WRONG" />,
                description,
            });

            if (!(error instanceof HttpError)) {
                throw error;
            }
        } finally {
            setIsUpdating(false);
        }
    }

    function openHideDefaultStatusModal(statusCode: string) {
        const availableStatuses = visibleStatuses.filter((statusItem) => statusItem.statusCode !== statusCode);
        const defaultReplacementStatus = availableStatuses.find((statusItem) => statusItem.isDefault);
        let selectedReplacementStatus = defaultReplacementStatus?.statusCode || availableStatuses[0]?.statusCode || '';

        modal.confirm({
            cancelButtonProps: {type: 'default'},
            cancelText: <Locale stringKey="BUTTON__CANCEL" />,
            content: (
                <div>
                    <div className={styles.ReviewStatus_modalDescription}>
                        <Locale stringKey="REVIEWS__STATUS__DEFAULT_DELETE_MODAL_DESCRIPTION" />
                    </div>
                    <Select
                        className={styles.ReviewStatus_modalSelect}
                        defaultValue={selectedReplacementStatus}
                        onChange={(value) => {
                            selectedReplacementStatus = value;
                        }}
                        placeholder={getLocalizedString('REVIEWS__STATUS__DELETE_MODAL_SELECT_PLACEHOLDER')}
                    >
                        {availableStatuses.map((statusItem) => (
                            <Option key={statusItem.statusCode} value={statusItem.statusCode}>
                                {statusItem.title}
                            </Option>
                        ))}
                    </Select>
                </div>
            ),
            maskClosable: true,
            okButtonProps: {type: 'primary', danger: true},
            okText: <Locale stringKey="REVIEWS__STATUS__DELETE_MODAL_CONFIRM" />,
            title: <Locale stringKey="REVIEWS__STATUS__DELETE_MODAL_TITLE" />,
            onOk: async () => {
                if (selectedReplacementStatus) {
                    await hideDefaultStatus(statusCode, selectedReplacementStatus);
                }
            },
        });
    }

    function handleDeleteStatus(event: React.MouseEvent, reviewStatus: ReviewStatusType) {
        event.stopPropagation();

        if (isCustomStatusDeletable(reviewStatus)) {
            openDeleteCustomStatusModal(reviewStatus.statusCode);

            return;
        }

        if (isDefaultStatusDeletable(reviewStatus)) {
            openHideDefaultStatusModal(reviewStatus.statusCode);
        }
    }

    function renderStatusOption(reviewStatus: ReviewStatusType) {
        const isDeleteActionInProgress = isDeletingStatusInProgress || isSavingHiddenStatuses;

        return (
            <div className={styles.ReviewStatus_optionContent}>
                <OverflowTitle className={styles.ReviewStatus_optionTitle} title={reviewStatus.title} />
                {isOwner && !mappedStatuses && isSystemDefaultStatus(reviewStatus) && (
                    <Tooltip title={<Locale stringKey="REVIEWS__STATUS__SYSTEM_STATUS_DELETE_TOOLTIP" />}>
                        <Button
                            className={styles.ReviewStatus_deleteButton}
                            disabled
                            icon={<FontAwesomeIcon icon={faXmark} />}
                            size="small"
                            type="text"
                        />
                    </Tooltip>
                )}
                {isOwner && !mappedStatuses && canShowStatusDeleteControl(reviewStatus) && (
                    <Tooltip title={<Locale stringKey="REVIEWS__STATUS__DELETE_STATUS_TOOLTIP" />}>
                        <Button
                            className={styles.ReviewStatus_deleteButton}
                            disabled={isDeleteActionInProgress}
                            icon={<FontAwesomeIcon icon={faXmark} />}
                            onClick={(event) => handleDeleteStatus(event, reviewStatus)}
                            size="small"
                            type="text"
                        />
                    </Tooltip>
                )}
            </div>
        );
    }

    function dropdownRender(menu: React.ReactElement) {
        return (
            <div>
                {menu}
                {isOwner && !mappedStatuses && (
                    <Flex>
                        {!isCreatingStatus ? (
                            <Button
                                className={styles.ReviewStatus_createButton}
                                onClick={handleCreateStatusClick}
                                ref={createButtonRef}
                                type="link"
                            >
                                <FontAwesomeIcon icon={faPlus} />
                                <Locale stringKey="REVIEWS__STATUS__CREATE_STATUS_BUTTON" />
                            </Button>
                        ) : (
                            <Space className={styles.ReviewStatus_createForm} direction="vertical">
                                <Input
                                    autoFocus
                                    className={styles.ReviewStatus_createFormInput}
                                    disabled={isCreatingStatusInProgress}
                                    onChange={(event_) => setNewStatusValue(event_.target.value)}
                                    onPressEnter={handleSaveNewStatus}
                                    placeholder={getLocalizedString('REVIEWS__STATUS__CREATE_STATUS_PLACEHOLDER')}
                                    style={{caretColor: 'auto'}}
                                    value={newStatusValue}
                                />
                                <Space className={styles.ReviewStatus_createFormButtons}>
                                    <Button
                                        disabled={isCreatingStatusInProgress}
                                        onClick={handleCancelCreateStatus}
                                        size="small"
                                    >
                                        <Locale stringKey="BUTTON__CANCEL" />
                                    </Button>
                                    <Button
                                        disabled={!newStatusValue.trim() || isCreatingStatusInProgress}
                                        loading={isCreatingStatusInProgress}
                                        onClick={handleSaveNewStatus}
                                        size="small"
                                        type="primary"
                                    >
                                        <Locale stringKey="BUTTON__SAVE" />
                                    </Button>
                                </Space>
                            </Space>
                        )}
                    </Flex>
                )}
            </div>
        );
    }

    return (
        <>
            <div className={classNames(className, styles.ReviewStatus, isOpen ? styles.ReviewStatus__focused : '')}>
                <ReviewPresenceBlockedTooltip
                    blockedAction={ReviewPresenceBlockedActionEnum.Status}
                    isBlocked={Boolean(isPresenceLocked)}
                >
                    <Tooltip
                        title={
                            isStatusMissingFromList ? (
                                <Locale stringKey="REVIEWS__STATUS__DELETED_STATUS_TOOLTIP" />
                            ) : null
                        }
                    >
                        <Select<Array<string>>
                            className={styles.ReviewStatus_select}
                            disabled={isStatusMissingFromList || isPresenceLocked}
                            dropdownRender={dropdownRender}
                            dropdownStyle={{minWidth: 320}}
                            filterOption={handleFilterOption}
                            getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                            loading={isReviewStatusListInProgress || isUpdating || isLoading}
                            onDropdownVisibleChange={handleCloseSelect}
                            onSelect={onStatusSelect}
                            open={isOpen}
                            optionFilterProp="children"
                            placeholder={<Locale stringKey="REVIEWS__FILTER__SELECT_A_STATUS" />}
                            showSearch
                            value={[
                                isStatusMissingFromList
                                    ? getLocalizedString('REVIEWS__STATUS__REPLACING_IN_PROGRESS')
                                    : status?.title || '',
                            ]}
                        >
                            {mappedStatuses
                                ? mappedStatuses.get(innerStatus)?.map((item) => (
                                      <Option key={item} value={item}>
                                          {getStatusByCode(item)?.title}
                                      </Option>
                                  ))
                                : visibleStatuses.map(
                                      (reviewStatus: ReviewStatusType): JSX.Element => (
                                          <Option
                                              key={reviewStatus.statusCode}
                                              label={reviewStatus.title}
                                              value={reviewStatus.statusCode}
                                          >
                                              {renderStatusOption(reviewStatus)}
                                          </Option>
                                      )
                                  )}
                        </Select>
                    </Tooltip>
                </ReviewPresenceBlockedTooltip>
            </div>
            {shouldShowTour && <Tour onClose={handleCloseTour} open={isTourOpen} steps={tourSteps} />}
        </>
    );
});
